import {clsx} from 'clsx'
import {useAnalytics} from '@github-ui/use-analytics'
import {FoldDownIcon, FoldUpIcon, UnfoldIcon} from '@primer/octicons-react'
import type {IconButtonProps} from '@primer/react'
import {useMemo} from 'react'

import useExpandHunk from '../hooks/use-expand-hunk'
import type {DiffLine} from '../types'
import {HunkCell} from './grid-cells/HunkCell'
import styles from './ExpandableHunkHeaderDiffLine.module.css'
import {DiffFeature, type DiffAnchor} from '@github-ui/diffs/types'
import type {HunkData} from '../helpers/hunk-data-helpers'
import {useFileLineCount, useHiddenFeatures} from '../stores/selectors'

export type PrunedIconButtonProps = Omit<IconButtonProps, 'aria-labelledby' | 'aria-label' | 'icon' | 'sx'>
type ExpandButtonIcon = typeof UnfoldIcon
type ExpandButtonLineProps = {
  direction: 'all' | 'down' | 'up'
  icon: ExpandButtonIcon
  isSplit: boolean
  label: string
  ariaLabel?: string
  onClick: () => void
} & PrunedIconButtonProps

function ExpandButtonLine({
  direction,
  icon: Icon,
  isSplit,
  label,
  onClick,
  ariaLabel,
  className,
  ...additionalProps
}: ExpandButtonLineProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  return (
    <button
      onClick={() => {
        onClick()
        sendAnalyticsEvent('file_entry.expand_hunk', 'FILE_EXPANDER_BUTTON')
      }}
      className={clsx(
        'Button Button--iconOnly Button--invisible',
        styles['expand-button-line'],
        !isSplit && styles['expand-button-unified'],
        className,
      )}
      aria-label={ariaLabel ?? label}
      data-direction={direction}
      {...additionalProps}
    >
      <Icon />
    </button>
  )
}

type ExpandButtonType = 'expand-all' | 'expand-up-and-down' | 'expand-down' | 'expand-up' | undefined

export interface ExpandableHunkHeaderDiffLineProps {
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: DiffLine
  isSplit: boolean
  nextHunk: HunkData | undefined
  nextLine?: DiffLine | null
  previousHunk: HunkData | undefined
  prevLine?: DiffLine | null
}

export default function ExpandableHunkHeaderDiffLine({
  currentHunk,
  diffAnchor,
  diffLine,
  isSplit,
  nextHunk,
  nextLine,
  previousHunk,
  prevLine,
}: ExpandableHunkHeaderDiffLineProps) {
  const currentLine = diffLine
  const {expandStartOfHunk, expandEndOfPreviousHunk} = useExpandHunk({currentHunk, nextHunk, previousHunk})
  const isStartOfDiff = (currentLine.position === 0 && currentLine.blobLineNumber !== 0) || !prevLine
  const isEndOfDiff = !nextLine
  const isStartOfFile = currentLine.blobLineNumber === 0
  const fileLineCount = useFileLineCount()
  const isStartOrEndOfDiff = isStartOfDiff || isEndOfDiff
  const isEndOfFile = isEndOfDiff && prevLine?.blobLineNumber === fileLineCount
  const maxRequestLineLimit = 20

  const showExpandAll = useMemo(() => {
    if (isStartOrEndOfDiff) return false
    if (currentLine.blobLineNumber <= 0) return false
    if (currentLine.blobLineNumber - prevLine.blobLineNumber <= maxRequestLineLimit) return true
    if (nextLine.blobLineNumber - currentLine.blobLineNumber >= maxRequestLineLimit) return true
    return false
  }, [isStartOrEndOfDiff, currentLine, nextLine, prevLine])

  const showExpandAboveAndBelow = useMemo(() => {
    if (isStartOrEndOfDiff) return false
    if (isEndOfFile) return false
    if (currentLine.blobLineNumber - prevLine.blobLineNumber > maxRequestLineLimit) return true
    return false
  }, [isStartOrEndOfDiff, isEndOfFile, currentLine, prevLine])

  const showExpandAbove = useMemo(() => {
    if (currentLine.blobLineNumber <= 0) return false
    if (isStartOfFile) return false
    if (isStartOfDiff) return true
    return false
  }, [isStartOfDiff, isStartOfFile, currentLine])

  const showExpandBelow = useMemo(() => {
    if (isStartOfDiff) return false
    if (isEndOfFile) return false
    if (isEndOfDiff) return true
    return false
  }, [isStartOfDiff, isEndOfDiff, isEndOfFile])

  const expandButtonType: ExpandButtonType = useMemo(() => {
    switch (true) {
      case showExpandAll:
        return 'expand-all'
      case showExpandAboveAndBelow:
        return 'expand-up-and-down'
      case showExpandAbove:
        return 'expand-up'
      case showExpandBelow:
        return 'expand-down'
    }
  }, [showExpandAll, showExpandAboveAndBelow, showExpandAbove, showExpandBelow])

  const handleExpandAboveClick = () => expandStartOfHunk()
  const handleExpandBelowClick = () => expandEndOfPreviousHunk()
  const handleExpandAllClick = () => expandStartOfHunk()

  const hiddenFeatures = useHiddenFeatures()

  const renderHunkButton = (additionalProps?: PrunedIconButtonProps) => {
    // If Expanded Lines feature is hidden return null
    if (hiddenFeatures.has(DiffFeature.expandLines)) return null

    switch (expandButtonType) {
      case 'expand-all':
        return (
          <ExpandButtonLine
            {...additionalProps}
            direction="all"
            icon={UnfoldIcon}
            isSplit={isSplit}
            label={`Expand file from line ${prevLine?.blobLineNumber || ''} to line ${nextLine?.blobLineNumber || ''}`}
            onClick={handleExpandAllClick}
          />
        )
      case 'expand-up-and-down':
        return (
          <div className="d-flex flex-column">
            <ExpandButtonLine
              {...additionalProps}
              direction="down"
              icon={FoldDownIcon}
              isSplit={isSplit}
              label={`Expand file down from line ${prevLine?.blobLineNumber || ''}`}
              className={styles['expand-up-and-down']}
              onClick={handleExpandBelowClick}
            />
            <ExpandButtonLine
              {...additionalProps}
              direction="up"
              icon={FoldUpIcon}
              isSplit={isSplit}
              label={`Expand file up from line ${nextLine?.blobLineNumber || ''}`}
              className={styles['expand-up-and-down']}
              onClick={handleExpandAboveClick}
            />
          </div>
        )
      case 'expand-up':
        return (
          <ExpandButtonLine
            {...additionalProps}
            direction="up"
            icon={FoldUpIcon}
            isSplit={isSplit}
            label={`Expand file up from line ${nextLine?.blobLineNumber || ''}`}
            onClick={handleExpandAboveClick}
          />
        )
      case 'expand-down':
        return (
          <ExpandButtonLine
            {...additionalProps}
            direction="down"
            icon={FoldDownIcon}
            isSplit={isSplit}
            label={`Expand file down from line ${prevLine?.blobLineNumber || ''}`}
            onClick={handleExpandBelowClick}
          />
        )
      default:
        return null
    }
  }

  return <HunkCell renderHunkButton={renderHunkButton} diffAnchor={diffAnchor} diffLine={diffLine} isSplit={isSplit} />
}
